import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { PersonAcademicService } from '../../services/person-academic.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { StackDirectives } from 'src/app/shared/directives/stack-directives';
import { MaterialModule } from 'src/app/material.module';
import { IListBox } from 'src/app/shared/interfaces/IClaveValor';
import { catchError, finalize, forkJoin, of, throwError } from 'rxjs';
import { MasterService } from 'src/app/shared/services/master.service';
import { MiAdmPersonasAcademicos } from '../../interfaces/IAdmPersonAcademic';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-modal-info-academica',
  imports: [ReactiveFormsModule, InputTextModule, StackDirectives, Select, MaterialModule, CommonModule],
  templateUrl: './modal-info-academica.html',
  styleUrl: './modal-info-academica.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ModalInfoAcademica {
  constructor(
    private dialogRef: MatDialogRef<ModalInfoAcademica>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      idEncrypt: string;
      codEmp: string;
      tipIde: string;
      nivAca: string;
      academicData: MiAdmPersonasAcademicos;
    }
  ) {}

  private loader = inject(LoaderService);
  private toastr = inject(ToastrService);
  private personAcademicService = inject(PersonAcademicService);
  private masterService = inject(MasterService);
  private ac = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);

  yearsList: IListBox[];
  monthsList: IListBox[];
  academicLevelsList: IListBox[];
  isEdit: boolean = false;

  form = this.fb.group({
    mesIni: ['', [Validators.required]],
    anoIni: ['', [Validators.required]],
    mesFin: ['', [Validators.required]],
    anoFin: ['', [Validators.required]],
    actEst: [false, [Validators.required]],
    idNivAca: ['', [Validators.required]],
    nomInst: ['', [Validators.required, Validators.maxLength(100)]],
    programa: ['', [Validators.required, Validators.maxLength(100)]]
  });

  ngOnInit() {
    this.loadData();
    this.cdr.markForCheck();
    this.form.markAllAsTouched();

    console.log('Data recibida en el modal:', this.data.idEncrypt);

    this.form.get('actEst')?.valueChanges.subscribe((isStudying) => {
      if (isStudying) {
        // Si estudia actualmente, quitar validadores y limpiar valores
        this.form.get('mesFin')?.clearValidators();
        this.form.get('anoFin')?.clearValidators();
        this.form.get('mesFin')?.setValue('');
        this.form.get('anoFin')?.setValue('');
      } else {
        // Si NO trabaja → agregar validadores
        this.form.get('mesFin')?.setValidators(Validators.required);
        this.form.get('anoFin')?.setValidators(Validators.required);
      }
      this.form.get('mesFin')?.updateValueAndValidity();
      this.form.get('anoFin')?.updateValueAndValidity();
    });
  }

  getErrorMessage(controlName: string, errorMessage: string): string | null {
    const control = this.form.get(controlName);
    if (control?.touched) {
      if (control.hasError('required')) {
        return errorMessage ?? 'Este campo es requerido';
      }
      if (control.hasError('pattern')) {
        return 'Solo se permiten números';
      }
      if (control.hasError('minlength')) {
        const minLength = control.getError('minlength').requiredLength;
        return `Mínimo ${minLength} caracteres`;
      }
      if (control.hasError('email')) {
        return 'Correo electrónico inválido';
      }
    }
    return null;
  }

  closeDialog(success: boolean) {
    this.dialogRef.close(success);
  }

  loadData() {
    this.loader.show('Cargando datos...');
    forkJoin({
      academicLevels: this.masterService
        .getAcademicLevels()
        .pipe(catchError((err) => (err.status === 204 ? of({ data: [] }) : throwError(() => err)))),
      years: of(this.generateYearsList()),
      months: of(this.generateMonthsList())
    })
      .pipe(finalize(() => this.loader.hide()))
      .subscribe({
        next: ({ academicLevels, years, months }) => {
          this.academicLevelsList = academicLevels.data;
          this.yearsList = years;
          this.monthsList = months;

          // Si hay datos académicos, llenar el formulario
          if (this.data.academicData) {
            this.isEdit = true;
            this.form.patchValue({
              mesIni: this.data.academicData.mesInicio?.toString(),
              anoIni: this.data.academicData.anioInicio?.toString(),
              mesFin: this.data.academicData.mesFin ? this.data.academicData.mesFin.toString() : '',
              anoFin: this.data.academicData.anioFin ? this.data.academicData.anioFin.toString() : '',
              actEst: this.data.academicData.actualmenteEstudiando,
              idNivAca: this.data.academicData.idNivelAcademico.toString(),
              nomInst: this.data.academicData.institucion,
              programa: this.data.academicData.programa
            });
          }
          this.loader.hide();
        },
        error: (e) => {
          // console.log('Error', e);
        }
      });
  }

  generateMonthsList(): IListBox[] {
    const months = [
      'Enero',
      'Febrero',
      'Marzo',
      'Abril',
      'Mayo',
      'Junio',
      'Julio',
      'Agosto',
      'Septiembre',
      'Octubre',
      'Noviembre',
      'Diciembre'
    ];

    return months.map((month, index) => ({
      label: month,
      id: (index + 1).toString()
    }));
  }

  generateYearsList(): IListBox[] {
    const currentYear = new Date().getFullYear();
    const years: IListBox[] = [];

    for (let i = 0; i <= 50; i++) {
      const year = currentYear - i;
      years.push({
        label: year.toString(),
        id: year.toString()
      });
    }

    return years;
  }

  onCreate() {
    if (this.form.valid) {
      this.loader.show('Guardando nivel académico...');
      const payload = this.setAcademicData();
      // console.log('Payload a enviar:', payload);
      this.personAcademicService.createFull(this.data.idEncrypt, payload).subscribe({
        next: (response) => {
          this.loader.hide();
          this.toastr.success('Nivel académico guardado con éxito');
          this.closeDialog(true);
        },
        error: (err) => {
          this.loader.hide();
          this.toastr.error('Error al guardar el nivel académico');
          console.error('Error al guardar el nivel académico:', err);
        }
      });
    } else {
      this.form.markAllAsTouched(); // Para mostrar errores
    }
  }

  onEdit() {
    if (this.form.valid) {
      this.loader.show('Actualizando nivel académico...');
      const payload = this.setAcademicData();
      console.log('Payload a enviar:', payload);
      this.personAcademicService.updateFull(this.data.idEncrypt, payload).subscribe({
        next: (response) => {
          this.loader.hide();
          this.toastr.success('Nivel académico actualizado con éxito');
          this.closeDialog(true);
        },
        error: (err) => {
          console.error('Error al actualizar el nivel académico:', err);
        }
      });
    } else {
      this.form.markAllAsTouched(); // Para mostrar errores
    }
  }

  setAcademicData(): MiAdmPersonasAcademicos {
    const isStudying = this.form.value.actEst;
    return {
      id: this.isEdit ? this.data.academicData.id : 0,
      idTipoIdentificacion: Number.parseInt(this.data.tipIde),
      identificacion: this.data.codEmp,
      mesInicio: Number.parseInt(this.form.value.mesIni!),
      anioInicio: Number.parseInt(this.form.value.anoIni!),
      mesFin: isStudying ? null : Number.parseInt(this.form.value.mesFin!),
      anioFin: isStudying ? null : Number.parseInt(this.form.value.anoFin!),
      actualmenteEstudiando: this.form.value.actEst!,
      idNivelAcademico: Number.parseInt(this.form.value.idNivAca!),
      institucion: this.form.value.nomInst!,
      programa: this.form.value.programa!,
      idEstado: 1
    };
  }

  sinExperiencia() {
    this.form.patchValue({
      nomInst: 'Sin experiencia',
      programa: 'Sin experiencia',
      mesIni: new Date().getMonth().toString(),
      anoIni: new Date().getFullYear().toString(),
      mesFin: new Date().getMonth().toString(),
      anoFin: new Date().getFullYear().toString(),
      actEst: false,
      idNivAca: '1'
    });
    this.onCreate();
  }
}
